import {
  ShellIconImage,
  ShellIconMic,
  ShellIconPaperclip,
  ShellIconPause,
  ShellIconPlay,
  ShellIconPlus,
  ShellIconSend,
  ShellIconSmile,
  ShellIconTrash,
  Spinner,
  Textarea,
} from '@zrsystem/ui';
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactElement,
  type SubmitEvent,
} from 'react';

import { useAudioRecorder } from '@/shared/chat/use-audio-recorder.js';
import '@/shared/chat/chat-composer.css';

const COMPOSER_EMOJIS = [
  '😀',
  '😁',
  '😂',
  '😊',
  '😉',
  '😍',
  '😎',
  '🤔',
  '😅',
  '🙌',
  '👍',
  '🙏',
  '👏',
  '🔥',
  '❤️',
  '✅',
  '⭐',
  '🚗',
  '🔧',
  '👋',
] as const;

const WAVE_BARS = [8, 14, 10, 18, 12, 20, 9, 16, 11, 18, 8, 15, 12, 19, 10, 14] as const;

const DEFAULT_FILE_ACCEPT =
  'video/*,.mp4,.mov,.webm,.mkv,.3gp,application/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip';

export interface ChatComposerProps {
  readonly draft: string;
  readonly disabled: boolean;
  readonly mediaLocked?: boolean;
  readonly isSending: boolean;
  readonly isUploading: boolean;
  readonly errorMessage: string | null;
  readonly onError: (message: string) => void;
  readonly onDraftChange: (value: string) => void;
  readonly onSubmitText: () => void;
  readonly onSubmitAudio: (file: File, durationSeconds: number) => Promise<boolean>;
  readonly onAttachFile: (file: File) => void;
  readonly photoAccept?: string;
  readonly fileAccept?: string;
  readonly fileLabel?: string;
  readonly fileInputClassName?: string;
}

function formatComposerClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function ChatComposer({
  draft,
  disabled,
  mediaLocked = false,
  isSending,
  isUploading,
  errorMessage,
  onError,
  onDraftChange,
  onSubmitText,
  onSubmitAudio,
  onAttachFile,
  photoAccept = 'image/*',
  fileAccept = DEFAULT_FILE_ACCEPT,
  fileLabel = 'Arquivo',
  fileInputClassName,
}: ChatComposerProps): ReactElement {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const emojiRef = useRef<HTMLDivElement | null>(null);
  const attachRef = useRef<HTMLDivElement | null>(null);
  const { recording, paused, elapsedSeconds, start, stop, pause, resume, cancel } =
    useAudioRecorder();
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [submittingAudio, setSubmittingAudio] = useState(false);
  const locked = disabled || isSending || isUploading || submittingAudio;
  const hasDraft = draft.trim().length > 0;
  const showSend = recording || hasDraft;

  useEffect(() => {
    return () => {
      void cancel();
    };
  }, [cancel]);

  useEffect(() => {
    if (!emojiOpen && !attachOpen) return;
    function handlePointerDown(event: MouseEvent): void {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (emojiOpen && emojiRef.current?.contains(target) !== true) {
        setEmojiOpen(false);
      }
      if (attachOpen && attachRef.current?.contains(target) !== true) {
        setAttachOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [attachOpen, emojiOpen]);

  async function beginRecording(): Promise<void> {
    if (locked || mediaLocked || recording || hasDraft) return;
    setEmojiOpen(false);
    setAttachOpen(false);
    try {
      await start();
    } catch (error: unknown) {
      onError(error instanceof Error ? error.message : 'Não foi possível gravar o áudio.');
    }
  }

  async function sendRecording(): Promise<void> {
    if (locked) return;
    setSubmittingAudio(true);
    try {
      const recorded = await stop();
      if (recorded === null) {
        onError('Não foi possível gravar o áudio.');
        return;
      }
      await onSubmitAudio(recorded.file, recorded.durationSeconds);
    } finally {
      setSubmittingAudio(false);
    }
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (locked) return;
    if (recording) {
      void sendRecording();
      return;
    }
    if (!hasDraft) return;
    onSubmitText();
  }

  function handleDraftKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
      return;
    }
    event.preventDefault();
    if (locked || !hasDraft) return;
    onSubmitText();
  }

  function handleAttachChange(event: ChangeEvent<HTMLInputElement>): void {
    const list = event.target.files;
    // FileList is live: copy before clearing, or the selection disappears.
    const files = list === null ? [] : Array.from(list);
    event.target.value = '';
    if (files.length === 0 || locked || recording || mediaLocked) return;
    for (const file of files) {
      onAttachFile(file);
    }
  }

  function openFilePicker(accept: string): void {
    const input = fileInputRef.current;
    if (input === null || locked || recording || mediaLocked) return;
    setAttachOpen(false);
    input.accept = accept;
    input.click();
  }

  function insertEmoji(emoji: string): void {
    const field = textareaRef.current;
    if (field === null) {
      onDraftChange(`${draft}${emoji}`);
      setEmojiOpen(false);
      return;
    }
    const start = field.selectionStart;
    const end = field.selectionEnd;
    onDraftChange(`${draft.slice(0, start)}${emoji}${draft.slice(end)}`);
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      field.focus();
      const cursor = start + emoji.length;
      field.setSelectionRange(cursor, cursor);
    });
  }

  const fileInputClass =
    fileInputClassName === undefined
      ? 'chat-composer__file-input'
      : `chat-composer__file-input ${fileInputClassName}`;

  return (
    <form className="chat-composer" onSubmit={handleSubmit}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className={fileInputClass}
        onChange={handleAttachChange}
        tabIndex={-1}
        aria-hidden
      />
      <div
        className={
          recording ? 'chat-composer__pill chat-composer__pill--recording' : 'chat-composer__pill'
        }
      >
        {recording ? (
          <>
            <button
              type="button"
              className="chat-composer__icon"
              aria-label="Apagar gravação"
              disabled={locked}
              onClick={() => {
                void cancel();
              }}
            >
              <ShellIconTrash size={18} />
            </button>
            <span className="chat-composer__rec" aria-live="polite">
              <span
                className={
                  paused
                    ? 'chat-composer__rec-dot chat-composer__rec-dot--paused'
                    : 'chat-composer__rec-dot'
                }
              />
              <span className="chat-composer__rec-time">{formatComposerClock(elapsedSeconds)}</span>
            </span>
            <span
              className={
                paused ? 'chat-composer__wave chat-composer__wave--paused' : 'chat-composer__wave'
              }
              aria-hidden
            >
              {WAVE_BARS.map((height, index) => (
                <span key={index} style={{ height, animationDelay: `${index * 0.06}s` }} />
              ))}
            </span>
            <button
              type="button"
              className="chat-composer__icon"
              aria-label={paused ? 'Continuar gravação' : 'Pausar gravação'}
              disabled={locked}
              onClick={() => {
                if (paused) {
                  resume();
                  return;
                }
                pause();
              }}
            >
              {paused ? <ShellIconPlay size={18} /> : <ShellIconPause size={18} />}
            </button>
          </>
        ) : (
          <>
            <div className="chat-composer__pop" ref={attachRef}>
              <button
                type="button"
                className="chat-composer__icon"
                aria-label="Anexar"
                aria-expanded={attachOpen}
                disabled={locked || mediaLocked}
                onClick={() => {
                  setEmojiOpen(false);
                  setAttachOpen((current) => !current);
                }}
              >
                <ShellIconPlus size={20} />
              </button>
              {attachOpen ? (
                <div className="chat-composer__menu" role="menu" aria-label="Anexos">
                  <button
                    type="button"
                    role="menuitem"
                    className="chat-composer__menu-item"
                    onClick={() => {
                      openFilePicker(photoAccept);
                    }}
                  >
                    <ShellIconImage size={16} />
                    Fotos
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="chat-composer__menu-item"
                    onClick={() => {
                      openFilePicker(fileAccept);
                    }}
                  >
                    <ShellIconPaperclip size={16} />
                    {fileLabel}
                  </button>
                </div>
              ) : null}
            </div>
            <div className="chat-composer__pop" ref={emojiRef}>
              <button
                type="button"
                className="chat-composer__icon"
                aria-label="Inserir emoji"
                aria-expanded={emojiOpen}
                disabled={locked || mediaLocked}
                onClick={() => {
                  setAttachOpen(false);
                  setEmojiOpen((current) => !current);
                }}
              >
                <ShellIconSmile size={20} />
              </button>
              {emojiOpen ? (
                <div className="chat-composer__emoji-panel" role="listbox" aria-label="Emojis">
                  {COMPOSER_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="chat-composer__emoji-option"
                      onClick={() => {
                        insertEmoji(emoji);
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <Textarea
              ref={textareaRef}
              className="chat-composer__input"
              rows={1}
              maxLength={4000}
              placeholder="Digite uma mensagem"
              aria-label="Mensagem"
              value={draft}
              disabled={locked}
              onChange={(event) => {
                onDraftChange(event.target.value);
              }}
              onKeyDown={handleDraftKeyDown}
            />
            {showSend || mediaLocked ? null : (
              <span className="chat-composer__mic">
                <button
                  type="button"
                  className="chat-composer__icon"
                  aria-label="Mensagem de voz"
                  disabled={locked || mediaLocked}
                  onClick={() => {
                    void beginRecording();
                  }}
                >
                  <ShellIconMic size={20} />
                </button>
                <span className="chat-composer__mic-tip" role="tooltip">
                  Mensagem de voz
                </span>
              </span>
            )}
          </>
        )}
      </div>
      {showSend ? (
        <button
          type="submit"
          className="chat-composer__send"
          aria-label="Enviar mensagem"
          disabled={locked || (!recording && !hasDraft)}
        >
          {isSending || isUploading || submittingAudio ? (
            <Spinner aria-label="Enviando" />
          ) : (
            <ShellIconSend size={18} />
          )}
        </button>
      ) : null}
      {errorMessage !== null ? (
        <p className="chat-composer__error" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </form>
  );
}
